// Phase 2.13 — shared minimal API fetch helper used by every Phase-2 V2
// service. Reads the in-memory access token from AuthContextV2 and adds
// the JWT Authorization header + cookie credentials for refresh.
//
// Session management (the reason you should never see a stray "Invalid token"
// again): the access token is short-lived (1h) and lives only in memory; the
// refresh token is a 30d httpOnly cookie. When a request comes back 401
// because the access token expired, this client transparently:
//   1. POSTs /api/auth/refresh (cookie-based) to mint a fresh access token,
//   2. stores it, and
//   3. replays the original request exactly once.
// The whole app keeps working without a page reload. Concurrent 401s (a
// dashboard fires many requests at once) are coalesced into a SINGLE refresh
// call — critical, because the server rotates the refresh-token row on every
// use, so a second parallel refresh would hit a revoked row and log the user
// out. If the refresh itself fails (cookie truly expired/revoked), the token
// is cleared and the 401 propagates so the app bounces to /login.

import { getAccessToken } from '../contexts/AuthContextV2';
import { refreshSession } from './sessionRefresh';

const API_BASE = process.env.REACT_APP_API_URL ?? '';
const DEFAULT_TIMEOUT_MS = 15000;

// Endpoints that must never trigger the refresh-and-retry dance: the auth
// lifecycle itself. A 401 from these is authoritative (bad credentials /
// expired cookie) and retrying would loop.
const NO_REFRESH_PATHS = ['/api/auth/refresh', '/api/auth/login', '/api/auth/logout'];

// The on-401 refresh goes through the shared, app-wide `refreshSession` (see
// sessionRefresh.js) — the SAME coalesced promise the boot refresh uses — so
// the server's refresh-token cookie is rotated exactly once even when the
// boot refresh and a data-request 401 race at startup. This is what stops the
// intermittent "Invalid token".
async function refreshAccessToken() {
  const body = await refreshSession();
  return body.accessToken;
}

// One raw attempt. Returns the parsed body on 2xx; throws an Error carrying
// `.status`/`.body` otherwise. Kept separate so we can replay it verbatim
// after a successful token refresh.
async function attempt(path, options, token) {
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  const controller = options.signal ? null : new AbortController();
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  let res;
  try {
    res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers,
      credentials: 'include',
      cache: 'no-store',
      signal: options.signal ?? controller.signal,
    });
  } catch (err) {
    if (err && err.name === 'AbortError') {
      const e = new Error(`Request timed out after ${timeoutMs}ms: ${path}`);
      e.code = 'TIMEOUT';
      throw e;
    }
    throw err;
  } finally {
    if (timer) clearTimeout(timer);
  }
  let body = null;
  try { body = await res.json(); } catch { /* non-json */ }
  if (!res.ok) {
    const err = new Error(body?.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export async function apiFetch(path, options = {}) {
  const token = getAccessToken();
  try {
    return await attempt(path, options, token);
  } catch (err) {
    // Only intercept an expired-access-token 401. Anything else (403, 404,
    // 500, network, timeout) propagates unchanged. Never refresh the auth
    // lifecycle endpoints, and never retry more than once.
    const isExpiredAuth = err?.status === 401 && !NO_REFRESH_PATHS.includes(path) && !options._retried;
    if (!isExpiredAuth) throw err;
    let fresh;
    try {
      fresh = await refreshAccessToken();
    } catch {
      // Refresh failed — surface the original 401 so callers/route guards
      // send the user back to login.
      throw err;
    }
    return attempt(path, { ...options, _retried: true }, fresh);
  }
}
